import React from 'react';

import IconInBox from 'src/common/ui/components/IconInBox/IconInBox';
import {
  AcceptNextPatientAcceptMode,
  AcceptNextPatientCard,
  AcceptNextPatientProviderStatus,
  AcceptNextPatientScheduleItemType,
} from 'src/myDayToday/components/AcceptNextPatientCard';
import { FyiBanner } from 'src/myDayToday/components/FyiBanner';
import { UnsignedVisitNotesSection } from 'src/myDayToday/components/UnsignedVisitNotesSection';
import { ReactComponent as IconOpportunityBlue } from 'src/shared/icons/iconClockBlue.svg';
import { ReactComponent as IconIndividualBlue } from 'src/shared/icons/iconIndividualBlue.svg';
import { ReactComponent as IconMedicalBlue } from 'src/shared/icons/iconMedicalBlue.svg';
import { ReactComponent as IconSupportBlueSvg } from 'src/shared/icons/iconSupportBlue.svg';

type SharedIcon = {
  label: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

const sharedIcons: SharedIcon[] = [
  { label: 'iconIndividualBlue', Icon: IconIndividualBlue },
  { label: 'iconMedicalBlue', Icon: IconMedicalBlue },
  { label: 'iconSupportBlue', Icon: IconSupportBlueSvg },
  { label: 'iconOpportunityBlue', Icon: IconOpportunityBlue },
];

function IconSandbox() {
  return (
    <section style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
      <h3>Icon Sandbox</h3>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>IconInBox</h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <IconInBox>
          <IconInBox.Checkbox />
        </IconInBox>
        <IconInBox>
          <IconInBox.Star />
        </IconInBox>
        <IconInBox>
          <IconInBox.Clock />
        </IconInBox>
        <IconInBox>
          <IconInBox.Stethoscope />
        </IconInBox>
        <IconInBox>
          <IconInBox.People />
        </IconInBox>
        <IconInBox>
          <IconInBox.Equalizer />
        </IconInBox>
        <IconInBox>
          <IconInBox.CalendarToday />
        </IconInBox>
        <IconInBox>
          <IconInBox.Videocam />
        </IconInBox>
        <IconInBox>
          <IconInBox.Notes />
        </IconInBox>
      </div>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>Boulder Icons</h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {sharedIcons.map(({ label, Icon }) => (
          <div
            key={label}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
          >
            <Icon width={64} height={64} />
            <span style={{ fontFamily: '"Nunito", "Nunito Sans"', fontSize: 11, color: '#666' }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

const mockNow = new Date('2026-06-03T14:45:00');

const mockUnsignedVisits = [
  {
    id: 'visit-1',
    start: '2026-06-03T14:40:00',
    attendeesDisplay: [
      { userType: 'Patient', firstName: 'Sarah', lastName: 'Johnson', preferredName: null },
    ],
  },
  {
    id: 'visit-2',
    start: '2026-06-03T14:00:00',
    attendeesDisplay: [
      { userType: 'Patient', firstName: 'Robert', lastName: 'Lee', preferredName: null },
    ],
  },
];

export function Sandbox() {
  return (
    <>
      <IconSandbox />
      <FyiBannerSandbox />
      <UnsignedVisitNotesSandbox />
      <AcceptNextPatientCardSandbox />
    </>
  );
}

export function FyiBannerSandbox() {
  const mockUpcoming = {
    type: AcceptNextPatientScheduleItemType.ScheduledVisit,
    start: '2026-06-03T14:52:00',
    eventId: null,
    patient: { id: 'p1', firstName: 'Michael', lastName: 'Chen', preferredName: null },
  };

  const mockInProgress = {
    type: AcceptNextPatientScheduleItemType.ScheduledVisit,
    start: '2026-06-03T14:37:00',
    eventId: null,
    patient: { id: 'p2', firstName: 'Michael', lastName: 'Chen', preferredName: null },
  };

  return (
    <section style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
      <h3 style={{ fontFamily: '"Nunito", "Nunito Sans"' }}>FYI Banner</h3>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>
        Default — upcoming visit
      </h4>
      <div style={{ maxWidth: 752, marginBottom: 24 }}>
        <FyiBanner scheduleItem={mockUpcoming} now={mockNow} />
      </div>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>
        Urgent — visit in progress
      </h4>
      <div style={{ maxWidth: 752, marginBottom: 24 }}>
        <FyiBanner scheduleItem={mockInProgress} now={mockNow} />
      </div>
    </section>
  );
}

export function UnsignedVisitNotesSandbox() {
  return (
    <section style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
      <h3 style={{ fontFamily: '"Nunito", "Nunito Sans"' }}>Unsigned Visit Notes Section</h3>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>
        With unsigned visits
      </h4>
      <div style={{ maxWidth: 752, marginBottom: 24 }}>
        <UnsignedVisitNotesSection
          unsignedVisits={mockUnsignedVisits}
          now={mockNow}
          onVisitClick={id => console.log(`Clicked visit: ${id}`)}
        />
      </div>

      <h4 style={{ fontFamily: '"Nunito", "Nunito Sans"', marginBottom: 8 }}>
        With no unsigned visits (empty state)
      </h4>
      <div style={{ maxWidth: 752 }}>
        <UnsignedVisitNotesSection unsignedVisits={[]} now={mockNow} onVisitClick={() => {}} />
      </div>
    </section>
  );
}

// Fixed reference time for the sandbox so states are deterministic
const SANDBOX_NOW = new Date('2024-01-15T14:00:00');
const UPCOMING_START = '2024-01-15T14:15:00';
// 5 min in the past
const IN_PROGRESS_START = '2024-01-15T13:55:00';

const MOCK_PATIENT = { id: 'p1', firstName: 'Alex', lastName: 'Smith', preferredName: null };

const MOCK_UNSIGNED_VISITS = [
  {
    id: 'v1',
    start: '2024-01-15T13:15:00',
    attendeesDisplay: [
      {
        userType: 'Patient',
        firstName: 'Jordan',
        lastName: 'Lee',
        preferredName: 'Jay',
      },
    ],
  },
  {
    id: 'v2',
    start: '2024-01-15T12:00:00',
    attendeesDisplay: [
      {
        userType: 'Patient',
        firstName: 'Morgan',
        lastName: 'Chen',
        preferredName: null,
      },
    ],
  },
];

const noop = async () => {};
const noopClick = () => {};

type SandboxState = {
  label: string;
  section: Parameters<typeof AcceptNextPatientCard>[0]['section'];
};

const sandboxStates: SandboxState[] = [
  {
    label: '1. Offline',
    section: {
      status: AcceptNextPatientProviderStatus.Offline,
      acceptMode: null,
      nextAvailability: '2024-01-16T09:00:00',
      scheduleItem: null,
    },
  },
  {
    label: '2. Online — no patients, nothing upcoming',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: null,
      nextAvailability: null,
      scheduleItem: null,
    },
  },
  {
    label: '3. Online — no patients, upcoming scheduled visit',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: null,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.ScheduledVisit,
        start: UPCOMING_START,
        eventId: 'e1',
        patient: MOCK_PATIENT,
      },
    },
  },
  {
    label: '4. Online — no patients, upcoming break',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: null,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.ScheduledBreak,
        start: UPCOMING_START,
        eventId: null,
        patient: null,
      },
    },
  },
  {
    label: '5. Online — no patients, upcoming EOD',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: null,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.EndOfDay,
        start: UPCOMING_START,
        eventId: null,
        patient: null,
      },
    },
  },
  {
    label: '6. Patients in queue — ANY, nothing upcoming',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: AcceptNextPatientAcceptMode.Any,
      nextAvailability: null,
      scheduleItem: null,
    },
  },
  {
    label: '7. Patients in queue — ANY, in-progress scheduled visit',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: AcceptNextPatientAcceptMode.Any,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.ScheduledVisit,
        start: IN_PROGRESS_START,
        eventId: 'e2',
        patient: MOCK_PATIENT,
      },
    },
  },
  {
    label: '8. Patients in queue — FOLLOW_UP_ONLY, upcoming scheduled visit',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: AcceptNextPatientAcceptMode.FollowUpOnly,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.ScheduledVisit,
        start: UPCOMING_START,
        eventId: 'e3',
        patient: MOCK_PATIENT,
      },
    },
  },
  {
    label: '9. Patients in queue — FOLLOW_UP_ONLY, upcoming break',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: AcceptNextPatientAcceptMode.FollowUpOnly,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.ScheduledBreak,
        start: UPCOMING_START,
        eventId: null,
        patient: null,
      },
    },
  },
  {
    label: '10. Patients in queue — FOLLOW_UP_ONLY, upcoming EOD',
    section: {
      status: AcceptNextPatientProviderStatus.Online,
      acceptMode: AcceptNextPatientAcceptMode.FollowUpOnly,
      nextAvailability: null,
      scheduleItem: {
        type: AcceptNextPatientScheduleItemType.EndOfDay,
        start: UPCOMING_START,
        eventId: null,
        patient: null,
      },
    },
  },
];

export function AcceptNextPatientCardSandbox() {
  return (
    <section style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
      <h3 style={{ fontFamily: '"Nunito", "Nunito Sans"' }}>Visit Section States</h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 24,
        }}
      >
        {sandboxStates.map(({ label, section }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              style={{
                fontFamily: '"Nunito", "Nunito Sans"',
                fontSize: 12,
                fontWeight: 600,
                color: '#666',
              }}
            >
              {label}
            </span>
            <AcceptNextPatientCard
              section={section}
              unsignedVisits={MOCK_UNSIGNED_VISITS}
              onAccept={noop}
              onVisitClick={noopClick}
              isAccepting={false}
              currentTime={SANDBOX_NOW}
            />
          </div>
        ))}
        {/* Empty unsigned visit notes state */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span
            style={{
              fontFamily: '"Nunito", "Nunito Sans"',
              fontSize: 12,
              fontWeight: 600,
              color: '#666',
            }}
          >
            11. Unsigned visit notes — empty state
          </span>
          <AcceptNextPatientCard
            section={{
              status: AcceptNextPatientProviderStatus.Online,
              acceptMode: null,
              nextAvailability: null,
              scheduleItem: null,
            }}
            unsignedVisits={[]}
            onAccept={noop}
            onVisitClick={noopClick}
            isAccepting={false}
            currentTime={SANDBOX_NOW}
          />
        </div>
      </div>
    </section>
  );
}
